import {
  Component,
  ContentChild,
  Directive,
  HostListener,
  ElementRef,
  Output,
  EventEmitter,
  Renderer2,
  Input,
  AfterViewInit,
  OnDestroy,
  ViewChild,
  ChangeDetectorRef,
  NgZone,
} from '@angular/core';
import { CdkConnectedOverlay, ConnectedPosition } from '@angular/cdk/overlay';

@Directive({
  selector: '[dropdownContent]',
  standalone: false,
})
export class DropdownContentDirective {
  constructor(public elementRef: ElementRef) {}
}

@Directive({
  selector: '[dropdownToggle]',
  standalone: false,
})
export class DropdownToggleDirective {
  constructor(private dropdown: DropdownComponent, public elementRef: ElementRef) {}

  @HostListener('click', ['$event'])
  toggleDropdown(event: Event) {
    event.stopPropagation();
    this.dropdown.toggle();
  }
}

@Component({
  selector: 'app-dropdown',
  templateUrl: './dropdown.component.html',
  styleUrl: './dropdown.component.scss',
  standalone: false,
})
export class DropdownComponent implements AfterViewInit, OnDestroy {
  static openDropdown: DropdownComponent | null = null;
  static readonly zIndexDefault: number = 1056;

  @ContentChild(DropdownContentDirective, { static: false }) dropdownContent?: DropdownContentDirective;
  @ContentChild(DropdownToggleDirective, { static: false }) dropdownToggle?: DropdownToggleDirective;

  // Template refs (see HTML below)
  @ViewChild('connected', { static: false }) connectedOverlay?: CdkConnectedOverlay;
  @ViewChild('triggerEl', { static: false }) triggerEl?: ElementRef<HTMLElement>;

  @Input() disabled = false;
  @Input() removeArrow = false;
  @Input() isDropdownListFullWidth = false;
  @Input() width: number | undefined;
  @Input() minWidth: number | undefined;

  isOpen = false;
  dropdownWidth: number = 0;
  zIndex: number | undefined;
  firstTimeOpened = false;

  @Output() openChange = new EventEmitter<boolean>();
  @Output() dropdownClosed = new EventEmitter<boolean>();

  private resizeObserver?: ResizeObserver;
  private rafId?: number;

  constructor(private elementRef: ElementRef, private renderer: Renderer2, private cdr: ChangeDetectorRef, private zone: NgZone) {}


  ngAfterViewInit() {
    if (!this.removeArrow && this.dropdownToggle?.elementRef?.nativeElement) {
      const icon = this.renderer.createElement('i');
      this.renderer.addClass(icon, 'icon-padding');
      this.renderer.addClass(icon, 'fas');
      this.renderer.addClass(icon, 'fa-chevron-down');
      this.renderer.addClass(icon, 'scale-80');
      this.renderer.setAttribute(icon, 'id', 'icon');
      this.renderer.appendChild(this.dropdownToggle.elementRef.nativeElement, icon);
    }
  }


  toggle(): void {
    if (this.disabled) return;

    if (DropdownComponent.openDropdown && DropdownComponent.openDropdown !== this) {
      DropdownComponent.openDropdown.close();
    }

    this.isOpen = !this.isOpen;
    DropdownComponent.openDropdown = this.isOpen ? this : null;

    this.updateIcon();
    this.openChange.emit(this.isOpen);

    if (this.isOpen) {
      this.addDocumentListeners();

      queueMicrotask(() => {
        this.connectedOverlay?.overlayRef?.updatePosition();
        this.startObservingTriggerResize();
      });
    } else {
      this.removeDocumentListeners();
      this.stopObservingTriggerResize();
    }
  }


  close(emitEvent = true): void {
    if (this.disabled) return;

    const wasOpen = this.isOpen;
    this.isOpen = false;

    if (DropdownComponent.openDropdown === this) {
      DropdownComponent.openDropdown = null;
    }

    this.removeDocumentListeners();
    this.stopObservingTriggerResize();
    this.updateIcon();

    if (emitEvent && wasOpen) {
      this.dropdownClosed.emit(true);
    }
  }


  updateIcon() {
    if (!this.removeArrow && this.dropdownToggle?.elementRef?.nativeElement) {
      const icon = this.dropdownToggle.elementRef.nativeElement.querySelector('#icon');
      if (icon) {
        this.renderer.removeClass(icon, 'fa-chevron-down');
        this.renderer.removeClass(icon, 'fa-chevron-up');
        this.renderer.addClass(icon, this.isOpen ? 'fa-chevron-up' : 'fa-chevron-down');
      }
    }
  }

  private startObservingTriggerResize() {
    const el = this.triggerEl?.nativeElement;
    if (!el) return;
    this.stopObservingTriggerResize();
    this.zone.runOutsideAngular(() => {
      if (typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver(() => {
          if (this.rafId) cancelAnimationFrame(this.rafId);
          this.rafId = requestAnimationFrame(() => {
            this.connectedOverlay?.overlayRef?.updatePosition();
          });
        });
        this.resizeObserver.observe(el);
      }
    });
  }

  private stopObservingTriggerResize() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = undefined;
    }
    if (this.rafId) {
      cancelAnimationFrame(this.rafId);
      this.rafId = undefined;
    }
  }

  private getParentNode(el: HTMLElement | undefined): HTMLElement | undefined{
    const parent = this.renderer.parentNode(el as any) as unknown;
    if (!parent) return undefined;
    if ((parent as Node).nodeType !== Node.ELEMENT_NODE) return undefined;
    return parent as HTMLElement;
  }


  onOverlayAttach() {
    this.syncDropdownProperties();

    if (!this.firstTimeOpened) {
      this.zIndex = this.getZIndex() ?? DropdownComponent.zIndexDefault;
      this.firstTimeOpened = true;
    }
    if (this.zIndex) {
      const overlayEl = this.connectedOverlay?.overlayRef?.overlayElement;
      if (!overlayEl) return;

      let parent = this.getParentNode(overlayEl)
      while (parent) {
        if (parent.classList && parent.classList.contains('cdk-overlay-container')) break;
        parent = this.getParentNode(parent);
      }

      if (parent) {
        this.renderer.setStyle(parent, 'z-index', `${this.zIndex + 10}`);
      }
    }
  }


  private syncDropdownProperties() {
    if (this.triggerEl) {
      const w = this.triggerEl?.nativeElement?.getBoundingClientRect()?.width;
      this.dropdownWidth = Math.ceil(w);
    }
  }

  private getZIndex() {
    const el = this.triggerEl?.nativeElement;
    return el ? this.getNearestNumericZIndex(el) : undefined;
  }


  private getNearestNumericZIndex(el: HTMLElement | null): number | undefined {
    let cur: HTMLElement | null = el;

    while (cur) {
      const z = window.getComputedStyle(cur).zIndex;

      if (z !== 'auto') {
        const n = Number.parseInt(z, 10);
        if (!Number.isNaN(n)) return n;
      }

      const parent = this.getParentNode(cur);
      if (!parent) break;
      cur = parent;
    }

    return undefined;
  }

  private removeDocumentScrollListener?: () => void;
  private removeWindowResizeListener?: () => void;

  private addDocumentListeners() {

    this.zone.runOutsideAngular(() => {
      this.removeDocumentScrollListener = this.renderer.listen('document', 'scroll', () => {
        this.zone.run(() => this.close(false));
      });

      this.removeWindowResizeListener = this.renderer.listen('window', 'resize', () => {
        this.zone.run(() => this.close(false));
      });
    });
  }

  private removeDocumentListeners() {
    this.removeDocumentScrollListener?.();
    this.removeDocumentScrollListener = undefined;

    this.removeWindowResizeListener?.();
    this.removeWindowResizeListener = undefined;
  }


  ngOnDestroy(): void {
    this.removeDocumentListeners();
    this.stopObservingTriggerResize();
  }

  positions: ConnectedPosition[] = [
    { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom' },
    { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'top' },
    { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'bottom' },
  ];
}

@Directive({
  selector: '[dropdownItem]',
  standalone: false,
})
export class DropdownItemDirective {
  constructor(private dropdown: DropdownComponent) {}

  @HostListener('click', ['$event'])
  toggleDropdown(event: Event) {
    this.dropdown.close();
    event.stopPropagation();
  }
}
